import type {
  Agent,
  AgentSummary,
  CreateAgentRequest,
  UpdateAgentRequest,
} from '../types/agent.js';

const BASE_URL = '/api/agents';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const msg = typeof body.error === 'string' ? body.error : body.error?.message ?? `Request failed: ${response.status}`;
    throw new Error(msg);
  }
  return response.json() as Promise<T>;
}

async function assertOk(response: Response): Promise<void> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const msg = typeof body.error === 'string' ? body.error : body.error?.message ?? `Request failed: ${response.status}`;
    throw new Error(msg);
  }
}

export async function getAgents(): Promise<AgentSummary[]> {
  return fetchJson<AgentSummary[]>(BASE_URL);
}

export async function getMainAgent(): Promise<Agent> {
  return fetchJson<Agent>(`${BASE_URL}/main`);
}

export async function getAgent(id: number): Promise<Agent> {
  return fetchJson<Agent>(`${BASE_URL}/${id}`);
}

export async function createAgent(data: CreateAgentRequest): Promise<Agent> {
  return fetchJson<Agent>(BASE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateAgent(id: number, data: Partial<UpdateAgentRequest>): Promise<Agent> {
  return fetchJson<Agent>(`${BASE_URL}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteAgent(id: number): Promise<void> {
  const response = await fetch(`${BASE_URL}/${id}`, { method: 'DELETE' });
  await assertOk(response);
}

export async function uploadAvatar(id: number, blob: Blob): Promise<void> {
  const response = await fetch(`${BASE_URL}/${id}/avatar`, {
    method: 'PUT',
    headers: { 'Content-Type': blob.type },
    body: blob,
  });
  await assertOk(response);
}

export function getAvatarUrl(id: number): string {
  return `${BASE_URL}/${id}/avatar`;
}

export async function deleteAvatar(id: number): Promise<void> {
  const response = await fetch(`${BASE_URL}/${id}/avatar`, { method: 'DELETE' });
  await assertOk(response);
}
